import { Endpoints } from '@octokit/types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { GithubUserRepo, Navigations } from '../types';

export interface FileExploreState {
  isExploreOpen: boolean;
  isFileOption: boolean;
  hasExploreMenu: boolean;
  isOverviewOpen: boolean;

  setFileOption: (value: boolean) => void;
  setIsExploreOpen: (value: boolean) => void;
  setHasExpolreMenu: (value: boolean) => void;
  setIsOverviewOpen: (value: boolean) => void;
  closeExploreMenu: () => void;
  closeOverviewMenu: () => void;

  navigation: Navigations;
  setNavigation: (value: FileExploreState['navigation']) => void;

  slackChannel: string | null;
  setSlackChannel: (value: string | null) => void;

  slackAuthUrl: string;
  setSlackAuthUrl: (value: string) => void;

  githubAuthUrl: string;
  setGithubAuthUrl: (value: string) => void;

  githubAccess: boolean;
  setGithubAccess: (value: boolean) => void;

  githubRepos: Endpoints['GET /repos/{owner}/{repo}']['response']['data'][];
  setGithubRepos: (value: Endpoints['GET /repos/{owner}/{repo}']['response']['data'][]) => void;

  githubUserRepos: GithubUserRepo[];
  setGithubUserRepos: (value: GithubUserRepo[]) => void;

  onGitRepoSelect: (arg: {
    repo: Endpoints['GET /repos/{owner}/{repo}']['response']['data'];
  }) => void;
  setOnGitRepoSelect: (func: FileExploreState['onGitRepoSelect']) => void;

  integrationId: string;
  setIntegrationId: (value: string) => void;

  onRemoveSlackChannel: (arg: { id: string }) => Promise<{ status: 'success' | 'failed' }>;
  setOnRemoveSlackChannel: (func: FileExploreState['onRemoveSlackChannel']) => void;

  isBoardSettingsOpen: boolean;
  setIsBoardSettingsOpen: (value: boolean) => void;

  onRemoveGithubRepo: (arg: { repoName: string }) => Promise<{ status: 'success' | 'failed' }>;
  setOnRemoveGithubRepo: (func: FileExploreState['onRemoveGithubRepo']) => void;

  isSaveTemplateOpen: boolean;
  setIsSaveTemplateOpen: (value: boolean) => void;
}

export const useFileExploreStore = create<
  FileExploreState,
  [['zustand/devtools', FileExploreState]]
>(
  devtools((set) => ({
    isFileOption: false,
    isExploreOpen: false,
    hasExploreMenu: false,
    setHasExpolreMenu: (value) => set({ hasExploreMenu: value }),
    setIsExploreOpen: (value) => set({ isExploreOpen: value }),
    setFileOption: (value) => set({ isFileOption: value }),

    closeExploreMenu: () =>
      set({ isExploreOpen: false, navigation: 'home', isBoardSettingsOpen: false }),

    isOverviewOpen: false,
    setIsOverviewOpen: (value) => set({ isOverviewOpen: value }),
    closeOverviewMenu: () =>
      set({ isOverviewOpen: false, navigation: 'home', isBoardSettingsOpen: false }),

    navigation: 'home',
    setNavigation: (value) => set({ navigation: value }),

    slackChannel: null,
    setSlackChannel: (value) => set({ slackChannel: value }),

    slackAuthUrl: '',
    setSlackAuthUrl: (value) => set({ slackAuthUrl: value }),

    githubAuthUrl: '',
    setGithubAuthUrl: (value) => set({ githubAuthUrl: value }),

    githubAccess: false,
    setGithubAccess: (value) => set({ githubAccess: value }),

    githubRepos: [],
    setGithubRepos: (value) => set({ githubRepos: value }),

    githubUserRepos: [],
    setGithubUserRepos: (value) => set({ githubUserRepos: value }),

    onGitRepoSelect: async () => null,
    setOnGitRepoSelect: (func) => set({ onGitRepoSelect: func }),

    integrationId: '',
    setIntegrationId: (value) => set({ integrationId: value }),

    onRemoveSlackChannel: async () => ({ status: 'success' }),
    setOnRemoveSlackChannel: (func) => set({ onRemoveSlackChannel: func }),

    isBoardSettingsOpen: false,
    setIsBoardSettingsOpen: (value) => set({ isBoardSettingsOpen: value }),

    onRemoveGithubRepo: async () => ({ status: 'success' }),
    setOnRemoveGithubRepo: (func) => set({ onRemoveGithubRepo: func }),

    isSaveTemplateOpen: false,
    setIsSaveTemplateOpen: (value) => set({ isSaveTemplateOpen: value }),
  }))
);
